import Layer from "../api/model/snapshots/layers/Layer";
import {
    ComponentLayer,
    ScreenComponentLayer,
    VirtualComponentLayer
} from "../model/ComponentLayer";
import InspectLayer from "../model/InspectLayer";
import LayerDiffs from "../model/LayerDiffs";
import StyleguideData from "../model/StyleguideData";
import VersionDiffs from "../model/VersionDiffs";

import BasicRecord from "./BasicRecord";
import { findComponentLayer, findVirtualComponentLayer, SearchedComponent } from "./component";
import getLayerDiffs, { LayerDiffOptions } from "./getLayerDiffs";
import { calcTotalArea } from "./layer/rect";
import { memoizeLastResult } from "./memoize";

function processLayer<T extends Layer>(layer: T): T[] {
    if (layer.type === "group") {
        const layers = (layer.layers as T[]).flatMap(processLayer);

        if (!layer.interactionLevel || layer.interactionLevel === "bottom") {
            return [layer, ...layers];
        }

        if (layer.interactionLevel === "top") {
            return [...layers, layer];
        }

        return layers;
    }

    return [layer];
}

function isLayerDisabled(layer: Pick<Layer, "type" | "interactionLevel">) {
    return layer.type === "group" && !layer.interactionLevel;
}

function getLayerMappingOnSourceId(layers: Layer[]): BasicRecord<Layer> {
    return layers.flatMap(processLayer).reduce((acc, layer) => {
        const { sourceId } = layer;

        if (sourceId) {
            acc[sourceId] = layer;
        }

        return acc;
    }, {} as BasicRecord<Layer>);
}

function getLayerMappingOnHierarchy(
    layers: InspectLayer[],
    parentHierarchyId = "",
    mapping: BasicRecord<InspectLayer> = {}
): BasicRecord<InspectLayer> {
    layers.forEach(layer => {
        if (layer.sourceId) {
            const layerHierarchyId = createLayerIdByHierarchy(parentHierarchyId, layer, mapping);
            mapping[layerHierarchyId] = layer;
            if (layer.type === "group") {
                getLayerMappingOnHierarchy(layer.layers!, layerHierarchyId, mapping);
            }
        }
    });
    return mapping;
}

function createLayerIdByHierarchy(
    parentHierarchyId: string,
    layer: Layer,
    mapping: BasicRecord<unknown>,
    index = 0
): string {
    const uniqueId = `${parentHierarchyId}-${layer.type === "text" ? "text" : layer.name}${index ? `(${index})` : ""}`;
    if (mapping[uniqueId]) {
        return createLayerIdByHierarchy(parentHierarchyId, layer, mapping, index + 1);
    }

    return uniqueId;
}

function calculateRemovedLayers({
    otherLayers,
    otherLayersMapping,
    layersMapping,
    layersHierarchyMapping,
    versionDiffs,
    options = { parentHierarchyId: "", matchedLayers: {} }
}: {
    otherLayers: Layer[];
    otherLayersMapping: BasicRecord<Layer>;
    layersMapping: BasicRecord<Layer>;
    layersHierarchyMapping: BasicRecord<Layer>;
    versionDiffs: VersionDiffs;
    options?: LayerDiffOptions;
}) {
    const currentLayerMapping: BasicRecord<Layer> = {};
    otherLayers.forEach(currLayer => {
        const { sourceId, layers: childLayers, type } = currLayer;
        if (sourceId) {
            const layerHierarchyId = createLayerIdByHierarchy(
                options.parentHierarchyId,
                currLayer,
                currentLayerMapping
            );
            currentLayerMapping[layerHierarchyId] = currLayer;
            const newOptions: LayerDiffOptions = {
                ...options,
                parentHierarchyId: layerHierarchyId
            };
            const prevVersionLayer = otherLayersMapping[sourceId];
            if (prevVersionLayer) {
                const nextVersionHierarchyMatch = layersHierarchyMapping[layerHierarchyId];
                const nextVersionSourceIdMatch = layersMapping[sourceId];
                const match = nextVersionSourceIdMatch || nextVersionHierarchyMatch;
                const isMatchedBefore = match ? options.matchedLayers![match.sourceId!] : false;
                const nextVersionLayer = isMatchedBefore
                    ? undefined
                    : match;
                if (!nextVersionLayer) {
                    versionDiffs.removedLayers.add(sourceId);
                    newOptions.diffLayerChild = true;
                    if (options.diffLayerChild) {
                        versionDiffs.removedLayerChildren.add(sourceId);
                    }
                } else {
                    newOptions.matchedLayers![nextVersionLayer.sourceId!] = true;
                }
            }

            if (childLayers && type === "group") {
                calculateRemovedLayers({
                    otherLayers: childLayers,
                    otherLayersMapping,
                    layersMapping,
                    layersHierarchyMapping,
                    versionDiffs,
                    options: newOptions
                });
            }
        }
    });
}

function calculateLayerDiffs({
    layers,
    layersMapping,
    otherLayersMapping,
    otherLayersHierarchyMapping,
    versionDiffs,
    options = { parentHierarchyId: "", matchedLayers: {} }
}: {
    layers: Layer[];
    layersMapping: BasicRecord<Layer>;
    otherLayersMapping: BasicRecord<Layer>;
    otherLayersHierarchyMapping: BasicRecord<Layer>;
    versionDiffs: VersionDiffs;
    options?: LayerDiffOptions;
}) {
    const currentLayerMapping: BasicRecord<Layer> = {};
    layers.forEach(currLayer => {
        const { sourceId, layers: childLayers, type } = currLayer;
        if (sourceId) {
            const layerHierarchyId = createLayerIdByHierarchy(
                options.parentHierarchyId,
                currLayer,
                currentLayerMapping
            );
            currentLayerMapping[layerHierarchyId] = currLayer;
            const newOptions: LayerDiffOptions = {
                ...options,
                parentHierarchyId: layerHierarchyId
            };

            const layer = layersMapping[sourceId];
            const prevVersionHierarchyMatch = otherLayersHierarchyMapping[layerHierarchyId];
            const prevVersionSourceIdMatch = otherLayersMapping[sourceId];
            const match = prevVersionSourceIdMatch || prevVersionHierarchyMatch;
            const isMatchedBefore = match ? options.matchedLayers![match.sourceId!] : false;
            const otherLayer = isMatchedBefore
                ? undefined
                : match;

            if (layer) {
                if (!otherLayer) {
                    versionDiffs.newLayers.add(sourceId);
                    newOptions.diffLayerChild = true;
                    if (options.diffLayerChild) {
                        versionDiffs.newLayerChildren.add(sourceId);
                    }
                } else {
                    newOptions.matchedLayers![otherLayer.sourceId!] = true;
                    const layerDiffs = getLayerDiffs(layer, otherLayer, options);
                    if (Object.keys(layerDiffs).length) {
                        versionDiffs.changedLayers[sourceId] = layerDiffs;
                    }
                }
            }

            if (childLayers && type === "group") {
                if (layer && layer.layout) {
                    newOptions.ignorePos = true;
                }

                calculateLayerDiffs({
                    layers: childLayers,
                    layersMapping,
                    otherLayersMapping,
                    otherLayersHierarchyMapping,
                    versionDiffs,
                    options: newOptions
                });
            }
        }
    });
}

// get the differences of layers. Below is the explanation of the algorithm
// find the layers that are exist in layers but not in otherLayers.
// find the ones that are common but has different fields.
// TODO: do not forget assets
function calculateVersionDiffs(layers: Layer[], otherLayers: Layer[]): VersionDiffs {
    const versionDiffs: VersionDiffs = {
        newLayers: new Set<string>(),
        newLayerChildren: new Set<string>(),
        changedLayers: {} as BasicRecord<LayerDiffs>,
        removedLayers: new Set<string>(),
        removedLayerChildren: new Set<string>()
    };

    const layersMapping = getLayerMappingOnSourceId(layers);
    const layersHierarchyMapping = getLayerMappingOnHierarchy(layers as InspectLayer[]);
    const otherLayersMapping = getLayerMappingOnSourceId(otherLayers);
    const otherLayersHierarchyMapping = getLayerMappingOnHierarchy(otherLayers as InspectLayer[]);

    calculateRemovedLayers({
        otherLayers,
        otherLayersMapping,
        layersMapping,
        layersHierarchyMapping,
        versionDiffs
    });

    calculateLayerDiffs({
        layers,
        layersMapping,
        otherLayersMapping,
        otherLayersHierarchyMapping,
        versionDiffs
    });

    return versionDiffs;
}

const CHANGED_LAYERS_COUNT_SOFT_LIMIT = 10;
const CHANGED_LAYERS_COUNT_HARD_LIMIT = 200;
const CHANGED_AREA_LIMIT = 0.3;

function isTooManyDiffs(layers: Layer[], diffs: VersionDiffs) {
    const sourceIdMap = getLayerMappingOnSourceId(layers);

    const changedLayersCount = diffs.newLayers.size + Object.values(diffs.changedLayers).length;

    const overUpdatedLayersThreshold = changedLayersCount > CHANGED_LAYERS_COUNT_SOFT_LIMIT;

    // calcTotalArea function is has a big complexity,
    // for large number of layers it may cause performance issues.
    if (changedLayersCount > CHANGED_LAYERS_COUNT_HARD_LIMIT) {
        return true;
    }

    const addedAndOtherLayersRects = [...diffs.newLayers, ...(Object.keys(diffs.changedLayers))]
        .map(layerId => sourceIdMap[layerId])
        .map(layer => layer.rect);

    const modifiedArea = calcTotalArea(addedAndOtherLayersRects);

    const screenArea = layers[0].rect.width * layers[0].rect.height;

    const overUpdatedAreaThreshold = (modifiedArea / screenArea) > CHANGED_AREA_LIMIT;

    return overUpdatedLayersThreshold && overUpdatedAreaThreshold;
}

const getFlattenedLayersMemoized = memoizeLastResult(getFlattenedLayers);

function getFlattenedLayers({ layers }: { layers: Layer[]; }): Layer[] {
    return layers.flatMap(processLayer);
}

type ExtractComponentLayersParams = {
    layers: Layer[] | null | undefined;
    styleguidesData: StyleguideData[] | null;
};

type ExtractComponentLayersResult = {
    allComponentLayers: ScreenComponentLayer[] | VirtualComponentLayer[];
    virtualComponentLayers: VirtualComponentLayer[];
    componentLayers: ComponentLayer[];
    components: SearchedComponent[] | undefined;
};

const extractComponentLayersMemoized = memoizeLastResult(extractComponentLayers);

function extractComponentLayers({
    layers,
    styleguidesData
}: ExtractComponentLayersParams): ExtractComponentLayersResult {
    const allComponentLayers = [];
    const virtualComponentLayers: VirtualComponentLayer[] = [];
    const componentLayers: ComponentLayer[] = [];
    let components: SearchedComponent[] | undefined;

    if (layers) {
        components = [];
        const flattenedLayers = getFlattenedLayersMemoized({ layers }) as InspectLayer[];

        for (const layer of flattenedLayers) {
            const foundComponentLayer = findComponentLayer(layer, styleguidesData);
            const foundVirtualComponentLayer = findVirtualComponentLayer(layer, styleguidesData);
            if (foundComponentLayer) {
                const existedComponentLayer = componentLayers.find(
                    item => item.layer._id === foundComponentLayer.layer._id
                );

                if (!existedComponentLayer) {
                    const componentLayer = {
                        _id: foundComponentLayer.component._id,
                        componentBarrelId: foundComponentLayer.barrelId,
                        name: foundComponentLayer.component.name,
                        rect: foundComponentLayer.layer.absoluteRect,
                        layer: foundComponentLayer.layer,
                        variant: !!foundComponentLayer.component.variant
                    };

                    componentLayers.push(componentLayer);
                    allComponentLayers.push(componentLayer);

                    const existedComponent = components.find(item => item._id === foundComponentLayer.component._id);

                    // eslint-disable-next-line max-depth
                    if (!existedComponent) {
                        components.push({ ...foundComponentLayer.component, barrelId: foundComponentLayer.barrelId });
                    }
                }
            }

            if (foundVirtualComponentLayer) {
                const existedVirtualComponentLayer =
                    virtualComponentLayers.find(item => item.layer._id === foundVirtualComponentLayer._id);

                if (existedVirtualComponentLayer) {
                    continue;
                }

                const virtualComponentLayer = {
                    layer: foundVirtualComponentLayer,
                    rect: foundVirtualComponentLayer.absoluteRect
                };

                virtualComponentLayers.push(virtualComponentLayer);
                allComponentLayers.push(virtualComponentLayer);
            }
        }
    }

    return {
        allComponentLayers,
        virtualComponentLayers,
        componentLayers,
        components
    };
}

export {
    processLayer,
    isLayerDisabled,
    calculateVersionDiffs,
    createLayerIdByHierarchy,
    isTooManyDiffs,
    getFlattenedLayers,
    getFlattenedLayersMemoized,
    extractComponentLayers,
    extractComponentLayersMemoized,
    LayerDiffOptions
};
